import React, { useEffect, useState } from "react";

// Importing bootstrap components
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  InputGroup,
  Spinner,
} from "react-bootstrap";

// Importing Formik & Yup
import { Formik } from "formik";
import * as Yup from "yup";

// Importing react router dom
import { useNavigate } from "react-router-dom";

// Importing react redux
import { useDispatch, useSelector } from "react-redux";

// Importing auth slice
import { 
  useLoginMutation, 
  useRequestOTPMutation, 
  useVerifyOTPMutation 
} from "state/slices/auth/authApiSlice";
import { setCredentials } from "state/slices/auth/authSlice";

// Importing toast
import { showErrorToast, showSuccessToast } from "utils/toast";

// Importing logo
import Logo from "assets/images/logo.png";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [loginMethod, setLoginMethod] = useState("password");
  const [otpSent, setOtpSent] = useState(false);
  const [otpResendTimer, setOtpResendTimer] = useState(0);
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const otpInputRefs = [
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null),
    React.useRef(null)
  ];
  
  const [login, { isLoading: isLoginLoading }] = useLoginMutation();
  const [requestOTP, { isLoading: isRequestOTPLoading }] = useRequestOTPMutation();
  const [verifyOTP, { isLoading: isVerifyOTPLoading }] = useVerifyOTPMutation();
  
  const isLoading = isLoginLoading || isRequestOTPLoading || isVerifyOTPLoading;
  const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

  useEffect(() => {
    if (loggedUserInfo) {
      navigate("/");
    }
  }, [navigate, loggedUserInfo]);

  // OTP resend timer
  useEffect(() => {
    let interval = null;
    if (otpResendTimer > 0) {
      interval = setInterval(() => {
        setOtpResendTimer((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpResendTimer]);

  const onRequestOTP = async (email, { setFieldError }) => {
    try {
      const res = await requestOTP({
        email,
        skipToast: true,
      }).unwrap();
      
      showSuccessToast(res, res.message || "OTP has been sent to your email address.");
      setOtpSent(true);
      setOtpResendTimer(60); // 60 seconds cooldown
    } catch (err) {
      console.error("Request OTP error:", err);
      
      if (err?.data?.errors) {
        const errors = err.data.errors;
        if (errors.email) {
          setFieldError("email", errors.email);
        }
        const firstError = Object.values(errors)[0];
        if (firstError) {
          showErrorToast(firstError);
        }
      } else {
        showErrorToast(err);
      }
    }
  };

  const onVerifyOTP = async (values, { setFieldError }) => {
    try {
      const res = await verifyOTP({
        email: values.email,
        otp: values.otp,
        skipToast: true,
      }).unwrap();
      
      showSuccessToast(res, "Login successful!");
      dispatch(setCredentials(res.data));
      
      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (err) {
      console.error("Verify OTP error:", err);
      
      if (err?.data?.errors) {
        const errors = err.data.errors;
        if (errors.email) {
          setFieldError("email", errors.email);
        }
        if (errors.otp) {
          setFieldError("otp", errors.otp);
        }
        const firstError = Object.values(errors)[0];
        if (firstError) {
          showErrorToast(firstError);
        }
      } else {
        showErrorToast(err);
      }
    }
  };

  const onLoginSubmit = async (values, { setFieldError }) => {
    if (loginMethod === "otp") {
      if (!otpSent) {
        // Request OTP first
        await onRequestOTP(values.email, { setFieldError });
        return;
      } else {
        // Verify OTP
        await onVerifyOTP(values, { setFieldError });
        return;
      }
    }

    // Password login
    try {
      const res = await login({
        email: values.email,
        password: values.password,
        skipToast: true,
      }).unwrap();
      
      showSuccessToast(res, "Login successful!");
      dispatch(setCredentials(res.data));
      
      setTimeout(() => {
        navigate("/");
      }, 500);
    } catch (err) {
      console.error("Login error:", err);
      
      if (err?.data?.errors) {
        const errors = err.data.errors;
        if (errors.email) {
          setFieldError("email", errors.email);
        }
        if (errors.password) {
          setFieldError("password", errors.password);
        }
        const firstError = Object.values(errors)[0];
        if (firstError) {
          showErrorToast(firstError);
        } else {
          showErrorToast("Please correct the errors above");
        }
      } else {
        showErrorToast(err);
      }
    }
  };

  const handleResendOTP = async (email, { setFieldError }) => {
    if (otpResendTimer > 0) return;
    await onRequestOTP(email, { setFieldError });
  };

  const switchLoginMethod = (method) => {
    setLoginMethod(method);
    setOtpSent(false);
    setOtpResendTimer(0);
    setOtpValues(["", "", "", "", "", ""]);
  };

  const handleOtpChange = (index, value, setFieldValue) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtpValues = [...otpValues];
    newOtpValues[index] = value;
    setOtpValues(newOtpValues);

    // Update Formik field
    const otpString = newOtpValues.join("");
    setFieldValue("otp", otpString);

    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === "Backspace") {
      if (!otpValues[index] && index > 0) {
        otpInputRefs[index - 1].current?.focus();
      }
    }
    // Handle left arrow
    else if (e.key === "ArrowLeft" && index > 0) {
      otpInputRefs[index - 1].current?.focus();
    }
    // Handle right arrow
    else if (e.key === "ArrowRight" && index < 5) {
      otpInputRefs[index + 1].current?.focus();
    }
  };

  const handleOtpPaste = (e, setFieldValue) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    
    // Only process if it's 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const newOtpValues = pastedData.split("");
      setOtpValues(newOtpValues);
      setFieldValue("otp", pastedData);
      
      // Focus last input
      otpInputRefs[5].current?.focus();
    }
  };

  const getValidationSchema = () => {
    const baseSchema = {
      email: Yup.string()
        .email("Please enter a valid email address")
        .required("Email is required"),
    };

    if (loginMethod === "password") {
      return Yup.object().shape(baseSchema);
    } else {
      // OTP mode
      if (otpSent) {
        return Yup.object().shape({
          ...baseSchema,
          otp: Yup.string()
            .required("OTP is required")
            .length(6, "OTP must be 6 digits")
            .matches(/^\d+$/, "OTP must contain only numbers"),
        });
      } else {
        return Yup.object().shape(baseSchema);
      }
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  return (
    <div className="container-fluid auth-wrapper">
      <div className="col-xl-4 col-lg-5 col-md-7 col-sm-10 col-12">
      <div className="text-center mb-4">
            <img src={Logo} alt="Battersea Web Expert" className="img-fluid" style={{ maxWidth: '180px' }} />
          </div>
          
          <Card className="shadow-none">
            <Card.Body className="p-4 p-md-5">
              <Formik
                initialValues={{
                  email: "",
                  password: "",
                  otp: "",
                }}
                validationSchema={getValidationSchema()}
                onSubmit={onLoginSubmit}
                enableReinitialize
              >
                {({ values, handleChange, handleSubmit, touched, errors, setFieldError, setFieldValue }) => (
                  <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                    
                    {/* Login Method Selection */}
                    <Form.Group className="mb-4">
                      <Form.Label className="control-label fw-medium mb-3">
                        Sign in with
                      </Form.Label>
                      <div className="d-flex flex-column flex-sm-row gap-2 gap-md-3">
                        <div
                          onClick={() => switchLoginMethod('password')}
                          className={`flex-fill px-3 py-2 rounded-3 border ${
                            loginMethod === 'password' 
                              ? 'border-primary border-2 bg-primary bg-opacity-10' 
                              : 'border-2'
                          } ${isLoading ? 'opacity-50 pe-none' : 'cursor-pointer'}`}
                          style={{ 
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className={`rounded-circle border ${
                                loginMethod === 'password' 
                                  ? 'border-primary border-2 bg-primary' 
                                  : 'border-secondary border-2 bg-white'
                              }`}
                              style={{ 
                                width: '20px', 
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {loginMethod === 'password' && (
                                <div className="rounded-circle bg-white" style={{ width: '8px', height: '8px' }} />
                              )}
                            </div>
                            <span className="fw-medium">Password</span>
                          </div>
                        </div>
                        
                        <div
                          onClick={() => switchLoginMethod('otp')}
                          className={`flex-fill px-3 py-2 rounded-3 border ${
                            loginMethod === 'otp' 
                              ? 'border-primary border-2 bg-primary bg-opacity-10' 
                              : 'border-2'
                          } ${isLoading ? 'opacity-50 pe-none' : 'cursor-pointer'}`}
                          style={{ 
                            cursor: isLoading ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <div 
                              className={`rounded-circle border ${
                                loginMethod === 'otp' 
                                  ? 'border-primary border-2 bg-primary' 
                                  : 'border-secondary border-2 bg-white'
                              }`}
                              style={{ 
                                width: '20px', 
                                height: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {loginMethod === 'otp' && (
                                <div className="rounded-circle bg-white" style={{ width: '8px', height: '8px' }} />
                              )}
                            </div>
                            <span className="fw-medium">Email OTP</span>
                          </div>
                        </div>
                      </div>
                    </Form.Group>

                    {/* Email Field */}
                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="yourEmail" className="control-label fw-medium">
                        Email
                      </Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        id="youremail"
                        value={values.email}
                        onChange={handleChange}
                        isInvalid={touched.email && !!errors.email}
                        disabled={isLoading || (loginMethod === "otp" && otpSent)}
                        placeholder="Enter your email address"
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Password Field */}
                    {loginMethod === "password" && (
                      <Form.Group className="mb-3">
                        <Form.Label htmlFor="yourPassword" className="control-label fw-medium">
                          Password
                        </Form.Label>
                        <InputGroup hasValidation>
                          <Form.Control
                            type={passwordVisible ? "text" : "password"}
                            name="password"
                            id="password"
                            value={values.password}
                            onChange={handleChange}
                            isInvalid={touched.password && !!errors.password}
                            disabled={isLoading}
                            placeholder="Enter your password"
                            className="border-end-0"
                          />
                          <Button
                            variant="no-hover"
                            onClick={togglePasswordVisibility}
                            disabled={isLoading}
                            className="border-start-0 border"
                          >
                            <i className={`bi ${passwordVisible ? 'bi-eye-fill' : 'bi-eye-slash-fill'}`}></i>
                          </Button>
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    )}

                    {/* OTP Field */}
                    {loginMethod === "otp" && otpSent && (
                      <>
                        <Form.Group className="mb-3">
                          <Form.Label className="control-label fw-medium">
                            OTP Code
                          </Form.Label>
                          <div className="d-flex gap-2 gap-md-3 mb-2">
                            {otpValues.map((digit, index) => (
                              <Form.Control
                                key={index}
                                ref={otpInputRefs[index]}
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value, setFieldValue)}
                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                onPaste={(e) => handleOtpPaste(e, setFieldValue)}
                                disabled={isLoading}
                                className="text-center fw-semibold otp-input"
                                style={{
                                  width: '40px',
                                  aspectRatio: '1/1',
                                  fontSize: '1.1rem',
                                  padding: '0'
                                }}
                              />
                            ))}
                          </div>
                          <Form.Text className="text-muted small">
                            OTP sent to <span className="fw-medium">{values.email}</span>. Check your inbox.
                          </Form.Text>
                        </Form.Group>
                        
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              handleResendOTP(values.email, { setFieldError });
                              setOtpValues(["", "", "", "", "", ""]);
                              setFieldValue("otp", "");
                            }}
                            disabled={isLoading || otpResendTimer > 0}
                            className="p-0 text-decoration-none"
                          >
                            {otpResendTimer > 0 
                              ? `Resend OTP in ${otpResendTimer}s`
                              : "Resend OTP"}
                          </Button>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => {
                              setOtpSent(false);
                              setOtpResendTimer(0);
                              setOtpValues(["", "", "", "", "", ""]);
                              setFieldValue("otp", "");
                            }}
                            disabled={isLoading}
                            className="p-0 text-decoration-none"
                          >
                            Change Email
                          </Button>
                        </div>
                      </>
                    )}

                    {/* Submit Button */}
                    <div className="d-grid gap-2">
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                            />
                            {loginMethod === "otp" && !otpSent
                              ? "Sending OTP..."
                              : loginMethod === "otp" && otpSent
                              ? "Verifying..."
                              : "Logging in..."}
                          </>
                        ) : (
                          <>
                            {loginMethod === "otp" && !otpSent
                              ? "Send OTP"
                              : "Sign in"}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
      </div>
    </div>
  );
};

export default Login;